import _ from "lodash";
import PropTypes from "prop-types";
import React from "react";
import classes from "./LabelComponent.scss";
import { DetailsIcon } from "SvgComponents";
import {
  HelpBulbOutlined,
  DownloadOutlined,
  ChatOutlined,
  PencilLineOutlined,
  ChevronUpCircleOutlined,
  ChevronDownCircleOutlined,
  LockFilled,
} from "@toddle-edu/ds-icons";
import HelperModal from "UIComponents/HelperModal";
import I18nHOC from "UIComponents/I18nHOC";
import AnimateHeight from "UIComponents/AnimateHeight";
import classNames from "classnames";
import { IconButton, Badge, Button, Tooltip } from "@toddle-edu/ds-web";
import PlannerIcon from "./PlannerIcon";
import IconPicker from "GenericTableEditor/IconPicker";
import { getIconList } from "GenericTableEditor/IconPicker/IconPickerUtils";
import ACLStore from "lib/aclStore";
import ProfileCascade from "UIComponents/ProfileCascade";
import { getFormattedUserName } from "Utils/GetFormattedUserName";

const styles = {
  commentButtonStyle: {
    padding: "0px 5px",
  },
};

const PromptComponent = ({
  propmtsEnable,
  prompts,
  togglePropmts,
  promptsId,
  promptsName,
  testId,
  t,
  isSinglePageStyle,
  isSinglePageAndNewUnitView,
  showPromptsInUnitPlan,
}) => {
  if (isSinglePageAndNewUnitView && !showPromptsInUnitPlan) {
    return null;
  }
  return (
    <div
      className={
        isSinglePageStyle
          ? classes.promptsContainerV2
          : classes.promptsContainer
      }
      id={promptsId}
    >
      <div
        className={classes.propmtLabelArrowContainer}
        onClick={togglePropmts}
        data-test-id={`${testId}-promptCard`}
      >
        <div
          className={classNames(
            isSinglePageStyle
              ? classes.propmtLabelTextV2
              : classes.propmtLabelText,
            isSinglePageStyle ? "text-label" : "text-label-2"
          )}
        >
          {t("unitPlan:prompts")}
        </div>
        {isSinglePageStyle ? null : (
          <IconButton
            variant="plain"
            icon={
              propmtsEnable ? (
                <ChevronUpCircleOutlined />
              ) : (
                <ChevronDownCircleOutlined />
              )
            }
            testId={`${testId}-togglePrompt`}
          />
        )}
      </div>
      <AnimateHeight duration={300} height={propmtsEnable ? "auto" : 0}>
        <div className={classes.prompTextContainer}>
          <div
            className={classNames(
              isSinglePageStyle ? classes.promptTextV2 : classes.promptText,
              isSinglePageStyle ? "text-body" : "text-body-1"
            )}
            dangerouslySetInnerHTML={{ __html: prompts }}
          />
        </div>
      </AnimateHeight>
    </div>
  );
};
class LabelComponent extends React.PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      showHelperModal: false,
      propmtsEnable: true,
      showIconPickerModal: false,
      editIconOption: false,
    };
    this.iconPickerContainerRef = React.createRef();
    this.labelContainerRef = React.createRef();
  }

  componentDidMount() {
    const {
      registerLabelContainerRef,
      fieldKey,
      shouldRegisterLabelContainerRef,
    } = this.props;

    if (shouldRegisterLabelContainerRef) {
      registerLabelContainerRef(fieldKey, this.labelContainerRef);
    }
  }

  componentWillUnmount() {
    // Unregister this label
    const {
      registerLabelContainerRef,
      fieldKey,
      shouldRegisterLabelContainerRef,
    } = this.props;
    if (shouldRegisterLabelContainerRef) {
      registerLabelContainerRef(fieldKey, null);
    }
  }

  getCommentButton() {
    const {
      totalCommentCount,
      unreadCommentCount,
      onCommentClick,
      isCommentActive,
      commentIconSize,
      testId,
      label,
      isSinglePageAndNewUnitView,
    } = this.props;

    const isSinglePageStyle = isSinglePageAndNewUnitView;

    const ButtonComponent = () => {
      return (
        <Button
          size={commentIconSize}
          icon={<ChatOutlined dsVersion={isSinglePageStyle ? "2.0" : "1.0"} />}
          style={styles.commentButtonStyle}
          isActivated={isCommentActive}
          onClick={onCommentClick}
          testId={`${testId}-${_.camelCase(label)}-chatIcon`}
          dsVersion={isSinglePageStyle ? "2.0" : "1.0"}
          variant={isSinglePageStyle ? "neutral" : "plain"}
          type={isSinglePageStyle ? "plain" : ""}
        >
          {totalCommentCount}
        </Button>
      );
    };
    if (totalCommentCount > 0) {
      if (unreadCommentCount > 0) {
        return (
          <Badge placement="topLeft" type="dot" offset={[8, 12]}>
            <ButtonComponent />
          </Badge>
        );
      } else {
        return <ButtonComponent />;
      }
    } else {
      return (
        <IconButton
          icon={<ChatOutlined dsVersion={isSinglePageStyle ? "2.0" : "1.0"} />}
          dsVersion={isSinglePageStyle ? "2.0" : "1.0"}
          variant={isSinglePageStyle ? "neutral" : "plain"}
          type={isSinglePageStyle ? "plain" : ""}
          size={commentIconSize}
          isActivated={isCommentActive}
          onClick={onCommentClick}
          testId={`${testId}-${_.camelCase(label)}-chatIcon`}
        />
      );
    }
  }

  togglePropmts = () => {
    const { showPromptsInUnitPlan, isSinglePageAndNewUnitView } = this.props;
    if (showPromptsInUnitPlan && isSinglePageAndNewUnitView) return;
    this.setState({ propmtsEnable: !this.state.propmtsEnable });
  };

  toggleIconPickerModal = event => {
    event.stopPropagation(); // prevent right pane open/close behavior on clicking icon picker
    this.setState({ showIconPickerModal: !this.state.showIconPickerModal });
  };

  showEditIconOption = show => {
    this.setState({ editIconOption: show });
  };

  renderLock = () => {
    const { fieldLockedObject, t } = this.props;
    const cascadeStyle = {
      width: "24px",
      height: "24px",
    };
    if (fieldLockedObject) {
      const userInfo = _.get(fieldLockedObject, "lockedField.userInfo", {});
      const { id, firstName, lastName, profileImage } = userInfo;
      const userName = getFormattedUserName({
        user: userInfo,
        type: "staff",
      });

      return (
        <div>
          {!_.isEmpty(userInfo) && (
            <Tooltip
              tooltip={t("unitPlan:currently_editing_with_user", {
                user: userName,
              })}
            >
              <div className={classes.profile}>
                <div className={classes.lock}>
                  <LockFilled size="x-small" />
                </div>
                <ProfileCascade
                  items={[
                    {
                      id,
                      value: `${firstName} ${lastName}`,
                      imageUrl: profileImage,
                    },
                  ]}
                  length={1}
                  customStyle={cascadeStyle}
                />
              </div>
            </Tooltip>
          )}
        </div>
      );
    }
  };

  render() {
    const {
      isCommentActive,
      isHelpActive,
      unreadCommentCount,
      labelComponent,
      label,
      isLabelArchived,
      labelStyle,
      onHelpClick,
      subText,
      helperText,
      onCommentClick,
      showComment,
      showHelper,
      labelClass,
      subtextClass,
      subTextStyle,
      showDetails,
      isDetailsActive,
      labelIconContainerStyle = {},
      onDetailsClick,
      extraRightButtonComponent,
      showExtraRightButtonComponent,
      prompts,
      icon,
      commentButtonId,
      helpButtonId,
      promptsId,
      labelCoachmarkId,
      promptsName,
      t,
      isPageHeader,
      isSectionHeader,
      isFieldHeader,
      commentIconSize,
      customCommentIconVariant,
      showCommentIconAsActivated,
      renderBottomComp = null,
      labelRightIcon,
      showLabel,
      showSubtext,
      onPrintClick,
      showPrint,
      plannerIconProps,
      leftContainerStyle,
      labelContainerStyle,
      helpIconProps,
      allowIconEditing,
      containerParentRef,
      testId,
      unitViewType,
      isStage,
      isStep,
      labelSubLabelContainerStyles,
      fieldLockedObject,
      isSinglePageAndNewUnitView,
      isUnitPlan,
      mode,
      subLabel,
      extraLabelComponent,
      showPromptsInUnitPlan,
      fieldLockComponent,
    } = this.props;

    const {
      showHelperModal,
      propmtsEnable,
      showIconPickerModal,
      editIconOption,
    } = this.state;
    const { lockedField = null } = fieldLockedObject || {};
    const isSinglePageStyle = isSinglePageAndNewUnitView;

    const detailsClass = classNames(
      { [classes.labelDetails]: true },
      { [classes.iconGrey]: !isDetailsActive },
      { [classes.iconBlue]: isDetailsActive }
    );

    const labelClasses = classNames(
      {
        [classes.inputLabel]:
          true &&
          !isPageHeader &&
          !isSectionHeader &&
          !isFieldHeader &&
          !isSinglePageStyle,
        [classes.inputLabelV2]:
          true &&
          !isPageHeader &&
          !isSectionHeader &&
          !isFieldHeader &&
          isSinglePageStyle,
      },
      {
        "text-label-l":
          true &&
          !isPageHeader &&
          !isSectionHeader &&
          !isFieldHeader &&
          isSinglePageStyle &&
          isStep &&
          !isStage,
      },
      {
        "text-heading-5":
          true &&
          !isPageHeader &&
          !isSectionHeader &&
          !isFieldHeader &&
          isSinglePageStyle &&
          !isStep &&
          isStage,
      },
      {
        "text-label-l":
          true &&
          !isPageHeader &&
          !isSectionHeader &&
          !isFieldHeader &&
          isSinglePageStyle &&
          !isStep &&
          !isStage,
      },
      { [classes[labelClass]]: !!labelClass },
      { "heading-4": isPageHeader },
      { "heading-5": isSectionHeader },
      { "text-label-2": isFieldHeader },
      { [classes.fieldHeader]: isFieldHeader },
      { [classes.labelWithRightIcon]: !!labelRightIcon }
    );

    const subTextClasses = classNames(
      { [classes.subText]: true },
      { "text-subtitle-3": !isPageHeader && !isSinglePageStyle },
      { "text-body": !isPageHeader && isSinglePageStyle },
      { "text-subtitle-2": isPageHeader },
      { [classes[subtextClass]]: !!subtextClass }
    );

    const iconElement = (
      <PlannerIcon
        icon={icon}
        isSinglePageAndNewUnitView={isSinglePageAndNewUnitView}
        label={label}
        {...plannerIconProps}
      />
    );

    const isPrintEnabled = !ACLStore.can(
      "FeatureFlag:PreventPrintAndScreenshots"
    );

    const onUpdateIcon = ({ icon }) => {
      const iconName = _.get(icon, "metadata.iconName");
      this.props.onUpdateIcon && this.props.onUpdateIcon(iconName);
    };

    const editIconClasses = classNames(classes.editIconBackGround, {
      [classes.showEditIcon]: editIconOption,
    });

    const iconList = _.values(getIconList());

    return !!label || !!labelComponent ? (
      <div className={classes.container} style={labelContainerStyle}>
        {labelComponent ? (
          labelComponent
        ) : label ? (
          <div
            className={classNames(
              isSinglePageStyle
                ? classes.labelContainerV2
                : classes.labelContainer
            )}
            style={{
              marginBottom: icon && !isSinglePageStyle ? 20 : 0,
              ...labelIconContainerStyle,
            }}
            ref={this.labelContainerRef}
          >
            <div
              className={classNames({
                [classes.leftContainerV2]: isSinglePageStyle,
                [classes.leftContainer]: !isSinglePageStyle,
                [classes.verticalAlign]:
                  isSinglePageStyle && _.isEmpty(subText),
              })}
              style={leftContainerStyle}
            >
              {allowIconEditing && isPageHeader ? (
                <div
                  className={classes.iconPickerContainer}
                  ref={this.iconPickerContainerRef}
                >
                  <button
                    onClick={this.toggleIconPickerModal}
                    onMouseEnter={() => this.showEditIconOption(true)}
                    onMouseLeave={() => this.showEditIconOption(false)}
                  >
                    {iconElement}
                    <div className={editIconClasses}>
                      <div className={classes.editIcon}>
                        <PencilLineOutlined size="xx-small" variant="subtle" />
                      </div>
                    </div>
                  </button>
                  {showIconPickerModal ? (
                    <IconPicker
                      iconList={iconList}
                      onUpdateIcon={onUpdateIcon}
                      containerParentRef={containerParentRef}
                      childrenRef={this.iconPickerContainerRef}
                      toggleIconPicker={this.toggleIconPickerModal}
                      showUploadOption={false}
                      selectedIconName={
                        _.includes(icon, "DEFAULT_PLANNER_ICON")
                          ? _.get(_.first(iconList), "iconName")
                          : icon
                      }
                    />
                  ) : null}
                </div>
              ) : (
                iconElement
              )}
              <div
                className={classNames(
                  isSinglePageStyle
                    ? classes.labelAndSubLabelContainerV2
                    : classes.labelAndSubLabelContainer
                )}
                style={labelSubLabelContainerStyles}
              >
                <div
                  className={labelClasses}
                  style={labelStyle}
                  id={labelCoachmarkId}
                  data-test-id={testId}
                >
                  {showLabel ? (
                    <>
                      <div className={classes.labelWithExtraLabel}>
                        <span
                          dangerouslySetInnerHTML={{ __html: label }}
                        ></span>
                        {extraLabelComponent ?? null}
                      </div>
                      {isLabelArchived ? (
                        <span className={classes.archivedText}>
                          ({t("common:archived")})
                        </span>
                      ) : null}
                      {subLabel ? subLabel : null}
                    </>
                  ) : (
                    ""
                  )}

                  {labelRightIcon && (
                    <div className={classes.labelRightIconContainer}>
                      {labelRightIcon}
                    </div>
                  )}
                </div>
                {subText ? (
                  <div
                    style={subTextStyle}
                    className={subTextClasses}
                    dangerouslySetInnerHTML={{
                      __html: showSubtext ? subText : "",
                    }}
                  ></div>
                ) : null}
              </div>
            </div>

            <div
              className={classNames(classes.buttonContainer, {
                [classes.buttonContainerV2]: isSinglePageStyle,
              })}
            >
              {showDetails ? (
                <div className={detailsClass} onClick={() => onDetailsClick()}>
                  <div className={classes.detailIcon}>
                    <DetailsIcon />
                  </div>
                  {`See Selected PYP elements`}
                </div>
              ) : null}
              {showPrint && isPrintEnabled && (
                <IconButton
                  icon={<DownloadOutlined />}
                  size={"medium"}
                  variant={"plain"}
                  onClick={onPrintClick}
                  testId={`${testId}-download`}
                />
              )}
              {!isSinglePageStyle &&
                showExtraRightButtonComponent &&
                extraRightButtonComponent && (
                  <div className={classes.extraButton}>
                    {extraRightButtonComponent}
                  </div>
                )}
              {lockedField && isUnitPlan && mode === "edit"
                ? this.renderLock()
                : null}
              {fieldLockComponent && isUnitPlan && !lockedField
                ? fieldLockComponent
                : null}
              {showComment ? this.getCommentButton() : null}
              {showHelper ? (
                <IconButton
                  icon={
                    <HelpBulbOutlined
                      dsVersion={isSinglePageStyle ? "2.0" : "1.0"}
                    />
                  }
                  dsVersion={isSinglePageStyle ? "2.0" : "1.0"}
                  variant={isSinglePageStyle ? "neutral" : "plain"}
                  type={isSinglePageStyle ? "plain" : ""}
                  isActivated={isHelpActive}
                  size={"medium"}
                  onClick={() =>
                    onHelpClick
                      ? onHelpClick()
                      : this.setState({ showHelperModal: true })
                  }
                  id={helpButtonId}
                  {...helpIconProps}
                  testId={`${testId}-help`}
                />
              ) : null}
              {isSinglePageStyle &&
                showExtraRightButtonComponent &&
                extraRightButtonComponent && (
                  <div className={classes.extraButton}>
                    {extraRightButtonComponent}
                  </div>
                )}
            </div>
          </div>
        ) : null}

        {prompts ? (
          <PromptComponent
            propmtsEnable={propmtsEnable}
            prompts={prompts}
            togglePropmts={this.togglePropmts}
            promptsId={promptsId}
            promptsName={promptsName}
            t={t}
            testId={testId}
            isSinglePageStyle={isSinglePageStyle}
            showPromptsInUnitPlan={showPromptsInUnitPlan}
            isSinglePageAndNewUnitView={isSinglePageAndNewUnitView}
          />
        ) : null}
        {showHelperModal && (
          <HelperModal
            helperText={helperText}
            onClose={() => this.setState({ showHelperModal: false })}
          />
        )}

        {renderBottomComp ? renderBottomComp : null}
      </div>
    ) : null;
  }
}

LabelComponent.defaultProps = {
  labelStyle: {},
  labelContainerStyle: {},
  showHelper: false,
  label: "",
  subText: "",
  helperText: "",
  showComment: false,
  showPrint: false,
  showDetails: false,
  isCommentActive: false,
  isDetailsActive: false,
  isHelpActive: false,
  totalCommentCount: 0,
  unreadCommentCount: 0,
  labelClass: "",
  subtextClass: "",
  subTextStyle: {},
  commentIconColor: "",
  showEdit: false,
  prompts: "",
  promptsName: "",
  isPageHeader: false,
  isSectionHeader: false,
  isFieldHeader: false,
  commentIconSize: "medium",
  showCommentIconAsActivated: false,
  customCommentIconVariant: "",
  showExtraRightButtonComponent: true,
  showLabel: true,
  showSubtext: true,
  isLabelArchived: false,
  plannerIconProps: {},
  leftContainerStyle: {},
  helpIconProps: {},
  allowIconEditing: false,
  containerParentRef: {},
  labelSubLabelContainerStyles: {},
};

LabelComponent.propTypes = {
  subText: PropTypes.string,
  helperText: PropTypes.string,
  labelComponent: PropTypes.element,
  labelStyle: PropTypes.object,
  showHelper: PropTypes.bool,
  showComment: PropTypes.bool,
  onHelpClick: PropTypes.func,
  onPrintClick: PropTypes.func,
  onCommentClick: PropTypes.func,
  isCommentActive: PropTypes.bool,
  isHelpActive: PropTypes.bool,
  labelClass: PropTypes.string,
  subtextClass: PropTypes.string,
  onEditClick: PropTypes.func,
  showEdit: PropTypes.bool,
  prompts: PropTypes.any,
  isPageHeader: PropTypes.bool,
  isSectionHeader: PropTypes.bool,
  isFieldHeader: PropTypes.bool,
  commentIconSize: PropTypes.string,
  showCommentIconAsActivated: PropTypes.bool,
  showPrint: PropTypes.bool,
  customCommentIconVariant: PropTypes.string,
  labelRightElement: PropTypes.element,
  showExtraRightButtonComponent: PropTypes.bool,
  showLabel: PropTypes.bool,
  showSubtext: PropTypes.bool,
  isLabelArchived: PropTypes.bool,
  plannerIconProps: PropTypes.object,
  leftContainerStyle: PropTypes.object,
  labelSubLabelContainerStyles: PropTypes.object,
  labelContainerStyle: PropTypes.object,
  helpIconProps: PropTypes.object,
  allowIconEditing: PropTypes.bool,
  containerParentRef: PropTypes.object,
  registerLabelContainerRef: PropTypes.func,
};

export { LabelComponent };

export default I18nHOC({ resource: "unitPlan" })(LabelComponent);
